import { USER_ROUTES } from '@/constants';
import type { SkillNode } from '@/pages/admin-tools/components/skill-framework-chart-utils';
import { axiosBaseQuery } from '@/services/axios-base-query';
import { createApi } from '@reduxjs/toolkit/query/react';
import type {
  AddSkillRequest,
  ProjectSkillPayload,
  ProjectSkillResponse,
  SelfReviewedSkill,
  SkillFilterResponse,
  SkillRating,
} from '@/types';
// import { data } from 'react-router-dom';

export interface SkillFrameworkType {
  id: number;
  name: string;
  description: string;
  created_on: string;
}

export interface SkillFrameworkListItem extends SkillNode {
  skill_type: SkillFrameworkType;
}

export interface ParsedSkillFrameworkNode {
  name: string;
  description: string;
  level: number;
  parent?: string | number | null;
  children: ParsedSkillFrameworkNode[];
}

export interface ParsedSkillFrameworkResponse {
  skills_framework: ParsedSkillFrameworkNode[];
}

export interface BulkCreateSkillFrameworkNode {
  name: string;
  description: string;
  skill_type: string;
  children: BulkCreateSkillFrameworkNode[];
}

export interface BulkCreateSkillFrameworkPayload {
  skill_frameworks: BulkCreateSkillFrameworkNode[];
}

export interface BulkCreateSkillFrameworkResponse {
  message: string;
  created_frameworks: SkillNode[];
}

export const skillsApi = createApi({
  reducerPath: 'skillsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Skills'],
  endpoints: (builder) => ({
    parseSkillsFramework: builder.mutation<
      ParsedSkillFrameworkResponse,
      { tenant: string; data: FormData }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.PARSE_SKILLS_FRAMEWORK(tenant),
        method: 'POST',
        data,
      }),
    }),
    bulkCreateSkillFramework: builder.mutation<
      BulkCreateSkillFrameworkResponse,
      { tenant: string; data: BulkCreateSkillFrameworkPayload }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.BULK_CREATE_SKILL_FRAMEWORK(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Skills'],
    }),
    getSkillFramework: builder.query<SkillFrameworkListItem[], { tenant: string }>({
      query: ({ tenant }) => ({
        url: USER_ROUTES.GET_SKILL_FRAMEWORK(tenant),
        method: 'GET',
      }),
      providesTags: ['Skills'],
    }),
    getPublicSkillFramework: builder.query<
      SkillFrameworkListItem[],
      { tenant: string }
    >({
      query: ({ tenant }) => ({
        url: USER_ROUTES.GET_PUBLIC_SKILL_FRAMEWORK(tenant),
        method: 'GET',
      }),
      providesTags: ['Skills'],
    }),
    createSkillFramework: builder.mutation<
      SkillNode,
      {
        tenant: string;
        name: string;
        skill_type: number | null;
        description: string;
        parent?: number;
      }
    >({
      query: ({ tenant, name, skill_type, description, parent }) => ({
        url: USER_ROUTES.GET_SKILL_FRAMEWORK(tenant),
        method: 'POST',
        data: { name, skill_type, description, parent },
      }),
      invalidatesTags: ['Skills'],
    }),
    editSkillFramework: builder.mutation<
      SkillNode,
      {
        tenant: string;
        id: string | number;
        name: string;
        skill_type: number | null;
        description: string;
        parent?: number | null;
        rootId: string | number;
      }
    >({
      query: ({ tenant, id, name, skill_type, description, parent }) => ({
        url: USER_ROUTES.GET_SKILL_FRAMEWORK_BY_ID(tenant, id),
        method: 'PATCH',
        data: { name, skill_type, description, parent },
      }),
      invalidatesTags: (_result, _error, { rootId, id }) => [
        'Skills',
        { type: 'Skills', id: rootId },
        { type: 'Skills', id },
      ],
    }),
    getPublicSkillFrameworkTypes: builder.query<
      SkillFrameworkType[],
      { tenant: string }
    >({
      query: ({ tenant }) => ({
        url: USER_ROUTES.GET_PUBLIC_SKILL_FRAMEWORK_TYPES(tenant),
        method: 'GET',
      }),
    }),
    getOrgSkillFrameworkTypes: builder.query<
      SkillFrameworkType[],
      { tenant: string }
    >({
      query: ({ tenant }) => ({
        url: USER_ROUTES.GET_ORG_SKILL_FRAMEWORK_TYPES(tenant),
        method: 'GET',
      }),
    }),
    getSkillFrameworkById: builder.query<
      SkillNode,
      { tenant: string; id: string | number }
    >({
      query: ({ tenant, id }) => ({
        url: USER_ROUTES.GET_SKILL_FRAMEWORK_BY_ID(tenant, id),
        method: 'GET',
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'Skills', id }],
    }),
    publishSkillFramework: builder.mutation<
      SkillNode,
      { tenant: string; id: string | number; published: boolean }
    >({
      query: ({ tenant, id, published }) => ({
        url: USER_ROUTES.GET_SKILL_FRAMEWORK_BY_ID(tenant, id),
        method: 'PATCH',
        data: { published },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'Skills',
        { type: 'Skills', id },
      ],
    }),
    duplicateSkillFramework: builder.mutation<
      SkillNode,
      { tenant: string; id: string | number; rootId: string | number }
    >({
      query: ({ tenant, id }) => ({
        url: USER_ROUTES.DUPLICATE_SKILL_FRAMEWORK(tenant, id),
        method: 'GET',
      }),
      invalidatesTags: (_result, _error, { rootId }) => [
        'Skills',
        { type: 'Skills', id: rootId },
      ],
    }),
    deleteSkillFramework: builder.mutation<
      { success: boolean },
      { tenant: string; id: string | number; rootId: string | number }
    >({
      query: ({ tenant, id }) => ({
        url: USER_ROUTES.GET_SKILL_FRAMEWORK_BY_ID(tenant, id),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { rootId }) => [
        'Skills',
        { type: 'Skills', id: rootId },
      ],
    }),
    getFilteredSkills: builder.query<
      SkillFilterResponse,
      {
        tenant: string;
        params: Record<string, string | number | string[] | number[]>;
      }
    >({
      query: ({ tenant, params }) => ({
        url: USER_ROUTES.FILTER_SKILLS(tenant),
        method: 'GET',
        params,
      }),
    }),
    getClientScopes: builder.query<
      any[],
      { tenant: string }
    >({
      query: ({ tenant }) => ({
        url: USER_ROUTES.GET_CLIENT_SCOPES(tenant),
        method: 'GET',
      }),
    }),
    getClientsByScope: builder.query<
      any[],
      { tenant: string; scopeId?: number | string }
    >({
      query: ({ tenant, scopeId }) => ({
        url: scopeId 
          ? `${USER_ROUTES.GET_CLIENTS_BY_SCOPE(tenant)}?client_scope=${scopeId}` 
          : USER_ROUTES.GET_CLIENTS_BY_SCOPE(tenant),
        method: 'GET',
      }),
    }),
    addProjectSkill: builder.mutation<
      ProjectSkillResponse,
      { tenant: string; data: ProjectSkillPayload }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.INTERNAL_PROJECT_SKILLS(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Skills'],
    }),
    deleteProjectSkill: builder.mutation<void, { tenant: string; id: number }>({
      query: ({ tenant, id }) => ({
        url: USER_ROUTES.INTERNAL_PROJECT_SKILL_DETAIL(tenant, id),
        method: 'DELETE',
      }),
      invalidatesTags: ['Skills'],
    }),
    getPersonalProfileUpdate: builder.query<
      SkillRating[],
      { tenant: string; userId: string }
    >({
      query: ({ tenant, userId }) => ({
        url: USER_ROUTES.USER_SKILL_PERSONAL_PROFILE(tenant, userId),
        method: 'GET',
      }),
    }),
    getSelfReviewedSkills: builder.query<
      SelfReviewedSkill[],
      { tenant: string; userId: string }
    >({
      query: ({ tenant, userId }) => ({
        url: USER_ROUTES.USER_SKILL_SELF_REVIEWED(tenant, userId),
        method: 'GET',
      }),
    }),
    addSkill: builder.mutation<void, { tenant: string; data: AddSkillRequest[] }>({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.ADD_USER_SKILL(tenant),
        method: 'POST',
        data: { skills: data },
      }),
      invalidatesTags: ['Skills'],
    }),
    getUserSkillRating: builder.query<
      SkillRating[],
      { tenant: string; userId: string }
    >({
      query: ({ tenant, userId }) => ({
        url: USER_ROUTES.USER_SKILL_RATING(tenant, userId),
        method: 'GET',
      }),
      providesTags: ['Skills'],
    }),
    getUserSkillList: builder.query<
      SelfReviewedSkill[],
      { tenant: string; userId: string }
    >({
      query: ({ tenant, userId }) => ({
        url: USER_ROUTES.GET_USER_SKILLS(tenant, userId),
        method: 'GET',
      }),
      providesTags: ['Skills'],
    }),
    updateUserSkillRating: builder.mutation<
      void,
      { tenant: string; id: number; data: Partial<SkillRating> }
    >({
      query: ({ tenant, id, data }) => ({
        url: USER_ROUTES.UPDATE_USER_SKILL_RATING(tenant, id),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['Skills'],
    }),
    deleteUserSkillRating: builder.mutation<void, { tenant: string; id: number }>({
      query: ({ tenant, id }) => ({
        url: USER_ROUTES.DELETE_USER_SKILL_RATING(tenant, id),
        method: 'DELETE',
      }),
      invalidatesTags: ['Skills'],
    }),
  }),
});

export const {
  useParseSkillsFrameworkMutation,
  useBulkCreateSkillFrameworkMutation,
  useGetSkillFrameworkQuery,
  useGetPublicSkillFrameworkQuery,
  useCreateSkillFrameworkMutation,
  useGetPublicSkillFrameworkTypesQuery,
  useGetOrgSkillFrameworkTypesQuery,
  useEditSkillFrameworkMutation,
  useGetSkillFrameworkByIdQuery,
  usePublishSkillFrameworkMutation,
  useDuplicateSkillFrameworkMutation,
  useDeleteSkillFrameworkMutation,
  useGetFilteredSkillsQuery,
  useGetClientScopesQuery,
  useGetClientsByScopeQuery,
  useAddProjectSkillMutation,
  useDeleteProjectSkillMutation,
  useGetPersonalProfileUpdateQuery,
  useGetSelfReviewedSkillsQuery,
  useAddSkillMutation,
  useGetUserSkillRatingQuery,
  useGetUserSkillListQuery,
  useUpdateUserSkillRatingMutation,
  useDeleteUserSkillRatingMutation,
} = skillsApi;
